/**
 * Today's Sessions List
 * Displays recent reading sessions (last 3-5)
 * Enhanced with better visual hierarchy
 */

import { useQuery } from '@tanstack/react-query';
import { sessionsService } from '@/services/sessions.service';
import type { Session } from '@/types/sessions';
import { BookOpen, ArrowRight, Sun, Moon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';

function formatSessionTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('th-TH', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return '';
  }
}

function formatRelativeTime(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 1) return 'เมื่อสักครู่';
    if (diffMins < 60) return `${diffMins} นาทีที่แล้ว`;
    if (diffHours < 24) return `${diffHours} ชม.ที่แล้ว`;
    if (diffDays === 1) return 'เมื่อวาน';
    if (diffDays < 7) return `${diffDays} วันที่แล้ว`;
    return d.toLocaleDateString('th-TH');
  } catch {
    return '';
  }
}

function isDaytime(iso: string): boolean {
  try {
    const d = new Date(iso);
    // แปลงเวลาเป็นเวลาไทย (UTC+7)
    const thaiTime = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Bangkok' }));
    const hour = thaiTime.getHours();
    // กลางวัน: 6:00 - 18:00
    return hour >= 6 && hour < 18;
  } catch {
    return true; // default เป็นกลางวัน
  }
}

function SessionItem({ session }: { session: Session }) {
  const timeStr = formatSessionTime(session.created_at);
  const relativeStr = formatRelativeTime(session.created_at);
  const isDay = isDaytime(session.created_at);
  
  // เลือก icon และสีตามช่วงเวลา
  const TimeIcon = isDay ? Sun : Moon;
  const iconBgClass = isDay 
    ? 'bg-linear-to-br from-orange-100 to-yellow-50 dark:from-orange-950/30 dark:to-yellow-950/20' 
    : 'bg-linear-to-br from-indigo-100 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/20';
  const iconColorClass = isDay 
    ? 'text-orange-500 dark:text-orange-400' 
    : 'text-indigo-500 dark:text-indigo-400';

  return (
    <Card className={cn(
      'transition-all duration-200 hover:shadow-md hover:border-primary/30',
      'bg-card border-border'
    )}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          {/* Icon - Dynamic based on time of day */}
          <div className={cn(
            "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl",
            iconBgClass,
            iconColorClass
          )}>
            <TimeIcon className="h-6 w-6" />
          </div>

          {/* Content */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm font-semibold text-foreground">
                เซสชัน
              </p>
              <span className="text-xs text-muted-foreground">
                {timeStr} • {relativeStr}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-sm">
              <span className="font-semibold text-primary">
                หน้า {session.start_page.toLocaleString('th-TH')}
              </span>
              <ArrowRight className="h-3 w-3 text-muted-foreground" />
              <span className="font-semibold text-primary">
                {session.end_page.toLocaleString('th-TH')}
              </span>
              <span className="text-xs text-muted-foreground ml-auto">
                ({session.pages_read.toLocaleString('th-TH')} หน้า)
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const LIMIT = 5;

export function TodaySessionsList() {
  const { data, isLoading } = useQuery({
    queryKey: ['sessions', 'recent'],
    queryFn: () => sessionsService.list({ limit: LIMIT }),
  });

  const sessions = data?.data ?? [];

  return (
    <div className="space-y-3">
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-2xl bg-muted" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-1/3" />
                    <div className="h-3 bg-muted rounded w-2/3" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : sessions.length === 0 ? (
        <Card className="border-2 border-dashed border-border/50">
          <CardContent className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted/50 mb-3">
              <BookOpen className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground mb-1">
              ยังไม่มีเซสชัน
            </p>
            <p className="text-xs text-muted-foreground">
              กดปุ่ม + ด้านล่างเพื่อบันทึกการอ่าน
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {sessions.map((session) => (
            <SessionItem key={session.id} session={session} />
          ))}
        </div>
      )}
    </div>
  );
}

export default TodaySessionsList;
